import React from 'react';
import { Phone, Mail, Globe, ArrowUp, Linkedin, Twitter, Facebook, Youtube, ChevronRight, Award, Briefcase } from 'lucide-react';
import { Logo } from '../ui/Logo';
import { companyInfo } from '../../data/companyInfo';
import { servicesData } from '../../data/servicesData';

interface FooterProps {
  onNavigate: (path: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Synchronized with Header main navigation
  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Portfolio', path: '/portfolio' },
    { label: 'About Us', path: '/about' },
    { label: 'Careers (We are Hiring)', path: '/careers' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <footer className="bg-[#050A13] text-[#D5DFEC] border-t border-[#1b2f4a] pt-14 pb-24 md:pb-8 relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#159DE8]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-[#FF851B]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-[#1b2f4a]">
          
          {/* Column 1: Brand & Bio */}
          <div className="space-y-4">
            <button 
              onClick={() => onNavigate('/')} 
              className="text-left focus:outline-none focus:ring-2 focus:ring-[#FF851B] rounded-xl"
            >
              <Logo variant="footer" />
            </button>
            <p className="text-xs text-[#8FA0B8] leading-relaxed">
              Transforming businesses through AI-powered websites, mobile applications, enterprise ERP, and digital marketing strategies engineered for sustainable commercial growth.
            </p>

            <div className="pt-2">
              <span className="text-[11px] uppercase tracking-wider text-[#8FA0B8] font-semibold block mb-3">Connect With Us</span>
              <div className="flex items-center gap-2.5">
                {/* LinkedIn */}
                <a 
                  href={companyInfo.socials.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="LinkedIn"
                  title="Follow us on LinkedIn"
                  className="w-9 h-9 rounded-lg bg-[#0077B5] hover:bg-[#005f93] flex items-center justify-center text-white shadow-md hover:scale-105 transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 8.76c.92 0 1.66-.74 1.66-1.66a1.66 1.66 0 0 0-1.66-1.66 1.66 1.66 0 0 0-1.66 1.66c0 .92.74 1.66 1.66 1.66m1.39 9.74v-8.37H5.07v8.37h2.78z" />
                  </svg>
                </a>

                {/* Twitter / X */}
                <a 
                  href={companyInfo.socials.twitter} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Twitter / X"
                  title="Follow us on X (Twitter)"
                  className="w-9 h-9 rounded-lg bg-[#000000] border border-[#1b2f4a] hover:border-[#159DE8] flex items-center justify-center text-white shadow-md hover:scale-105 transition-all"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>

                {/* Facebook */}
                <a 
                  href={companyInfo.socials.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Facebook"
                  title="Follow us on Facebook"
                  className="w-9 h-9 rounded-lg bg-[#1877F2] hover:bg-[#166fe5] flex items-center justify-center text-white shadow-md hover:scale-105 transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>

                {/* Instagram */}
                <a 
                  href={companyInfo.socials.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="Instagram"
                  title="Follow us on Instagram"
                  className="w-9 h-9 rounded-lg bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] hover:opacity-90 flex items-center justify-center text-white shadow-md hover:scale-105 transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>

                {/* YouTube */}
                <a 
                  href={companyInfo.socials.youtube} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label="YouTube"
                  title="Subscribe on YouTube"
                  className="w-9 h-9 rounded-lg bg-[#FF0000] hover:bg-[#d90000] flex items-center justify-center text-white shadow-md hover:scale-105 transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-white tracking-wider uppercase mb-4 border-l-2 border-[#FF851B] pl-2.5">
              Quick Links
            </h4>
            <ul className="space-y-2 text-xs">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => onNavigate(link.path)}
                    className="text-[#8FA0B8] hover:text-[#FF851B] transition-colors flex items-center gap-1.5 group"
                  >
                    <ChevronRight size={12} className="text-[#8FA0B8]/40 group-hover:text-[#FF851B] transition-colors" />
                    <span>{link.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Our Services */}
          <div>
            <h4 className="text-sm font-semibold text-white tracking-wider uppercase mb-4 border-l-2 border-[#159DE8] pl-2.5">
              Our Services
            </h4>
            <ul className="space-y-2 text-xs">
              {servicesData.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => onNavigate(`/services/${s.slug}`)}
                    className="text-[#8FA0B8] hover:text-[#159DE8] transition-colors flex items-center gap-1.5 group text-left"
                  >
                    <ChevronRight size={12} className="text-[#8FA0B8]/40 group-hover:text-[#159DE8] transition-colors shrink-0" />
                    <span>{s.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact Information */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-white tracking-wider uppercase mb-4 border-l-2 border-[#159DE8] pl-2.5">
              Contact Us
            </h4>
            <div className="space-y-3 text-xs text-[#8FA0B8]">
              <div className="flex items-start gap-2.5">
                <Phone size={15} className="text-[#FF851B] shrink-0 mt-0.5" />
                <div>
                  <span className="block text-[11px] text-[#8FA0B8]/80">Direct Inquiries:</span>
                  <a href={`tel:${companyInfo.contact.phone}`} className="text-white hover:text-[#FF851B] transition-colors font-medium">
                    {companyInfo.contact.phoneDisplay}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Mail size={15} className="text-[#159DE8] shrink-0 mt-0.5" />
                <div>
                  <span className="block text-[11px] text-[#8FA0B8]/80">Email Communications:</span>
                  <a href={`mailto:${companyInfo.contact.email}`} className="text-white hover:text-[#159DE8] transition-colors font-medium">
                    {companyInfo.contact.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Globe size={15} className="text-[#159DE8] shrink-0 mt-0.5" />
                <div>
                  <span className="block text-[11px] text-[#8FA0B8]/80">Global Services:</span>
                  <span className="text-[#D5DFEC] leading-relaxed block">
                    {companyInfo.contact.globalReach}
                  </span>
                </div>
              </div>

              {/* Secure Global Payment Badges */}
              <div className="pt-2 border-t border-[#1b2f4a]/70">
                <span className="block text-[11px] text-[#8FA0B8]/80 mb-2">Accepted Payment Options:</span>
                <div className="flex items-center gap-2.5">
                  {/* PayPal */}
                  <a
                    href={companyInfo.payments.paypalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Pay with PayPal"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#003087] hover:bg-[#002569] border border-[#004dc5]/40 text-white shadow-sm hover:scale-105 transition-all group"
                  >
                    <svg className="w-3.5 h-3.5 fill-[#0079C1]" viewBox="0 0 24 24">
                      <path d="M15.607 4.653H8.941L6.645 19.251H1.82L4.862 0h7.995c3.754 0 6.375 2.294 6.473 5.513-.648-.478-2.105-.86-3.722-.86m6.57 5.546c0 3.41-3.01 6.853-6.958 6.853h-2.493L11.595 24H6.74l1.845-11.538h3.592c4.208 0 7.346-3.634 7.153-6.949a5.24 5.24 0 0 1 2.848 4.686M9.653 5.546h6.408c.907 0 1.942.222 2.363.541-.195 2.741-2.655 5.483-6.441 5.483H8.714Z"/>
                    </svg>
                    <span className="text-xs font-bold tracking-tight text-white">PayPal</span>
                  </a>

                  {/* Stripe */}
                  <a
                    href={companyInfo.payments.stripeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Pay securely via Stripe"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#635BFF] hover:bg-[#5249e7] border border-[#857dff]/40 text-white shadow-sm hover:scale-105 transition-all group"
                  >
                    <svg className="w-3 h-3.5 fill-white" viewBox="0 0 24 24">
                      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/>
                    </svg>
                    <span className="text-xs font-bold tracking-tight text-white">Stripe</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#8FA0B8]">
          <div>
            © 2025 EBON Media Solutions. All rights reserved.
          </div>
          
          <div className="flex items-center gap-6">
            <button 
              onClick={() => onNavigate('/privacy')} 
              className="hover:text-white transition-colors"
            >
              Privacy Policy
            </button>
            <span>•</span>
            <button 
              onClick={() => onNavigate('/terms')} 
              className="hover:text-white transition-colors"
            >
              Terms & Conditions
            </button>
            <span>•</span>
            <button 
              onClick={scrollToTop} 
              className="flex items-center gap-1.5 text-[#159DE8] hover:text-[#FF851B] transition-colors ml-2"
              aria-label="Back to top"
            >
              <span>Back to Top</span>
              <ArrowUp size={13} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
